import { createContext, useCallback, useContext, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

export interface NavigationGuard {
    when: boolean;
    onBlocked?: () => void;
}

type PendingNavigation =
    | { kind: 'path'; to: string }
    | { kind: 'back'; fallbackTo: string };

interface NavigationGuardContextValue {
    registerGuard: (guard: NavigationGuard | null) => void;
    tryNavigate: (to: string) => void;
    tryGoBack: (fallbackTo?: string) => void;
    confirmPendingNavigation: () => void;
    cancelPendingNavigation: () => void;
}

const NavigationGuardContext = createContext<NavigationGuardContextValue | null>(null);

export const NavigationGuardProvider = ({ children }: { children: ReactNode }) => {
    const navigate = useNavigate();
    const guardRef = useRef<NavigationGuard | null>(null);
    const pendingNavigationRef = useRef<PendingNavigation | null>(null);

    const registerGuard = useCallback((guard: NavigationGuard | null) => {
        guardRef.current = guard;
    }, []);

    const tryNavigate = useCallback(
        (to: string) => {
            const guard = guardRef.current;
            if (guard?.when) {
                pendingNavigationRef.current = { kind: 'path', to };
                guard.onBlocked?.();
                return;
            }

            navigate(to);
        },
        [navigate],
    );

    const tryGoBack = useCallback(
        (fallbackTo = '/') => {
            const guard = guardRef.current;
            const goBack = () => {
                if (window.history.length > 1) {
                    navigate(-1);
                    return;
                }

                navigate(fallbackTo);
            };

            if (guard?.when) {
                pendingNavigationRef.current = { kind: 'back', fallbackTo };
                guard.onBlocked?.();
                return;
            }

            goBack();
        },
        [navigate],
    );

    const confirmPendingNavigation = useCallback(() => {
        const pending = pendingNavigationRef.current;
        pendingNavigationRef.current = null;
        guardRef.current = null;

        if (!pending) {
            return;
        }

        if (pending.kind === 'path') {
            navigate(pending.to);
            return;
        }

        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        navigate(pending.fallbackTo);
    }, [navigate]);

    const cancelPendingNavigation = useCallback(() => {
        pendingNavigationRef.current = null;
    }, []);

    return (
        <NavigationGuardContext.Provider
            value={{ registerGuard, tryNavigate, tryGoBack, confirmPendingNavigation, cancelPendingNavigation }}
        >
            {children}
        </NavigationGuardContext.Provider>
    );
};

export const useNavigationGuard = () => {
    const context = useContext(NavigationGuardContext);
    if (!context) {
        throw new Error('useNavigationGuard must be used within NavigationGuardProvider');
    }

    return context;
};

/** Returns null when used outside `NavigationGuardProvider` (e.g. login). */
export const useOptionalNavigationGuard = () => useContext(NavigationGuardContext);
